import React, { useMemo } from "react";
import { gql } from "@apollo/client";
import { useHrisCsvUploadSidebarQuery } from "./HrisCsvUploadDetailSidebar.generated";
import SidebarHeader, {
  SidebarContent,
  SidebarTitle,
} from "@src/features/sidebars/ui/SidebarHeader";
import InfoTable, { InfoTableItem } from "@src/lib/ui/InfoTable";
import SidebarLink from "@src/features/sidebars/ui/SidebarLink";
import ClickableLinkText from "@src/lib/ui/ClickableLinkText";
import Link from "next/link";
import { cn } from "@src/lib/utils/cn";
import { format, parseISO } from "date-fns";
import Text from "@src/lib/ui/Text";
import { DetailSidebarType } from "@src/features/sidebars/detailSidebar";
import FileOutlineIcon from "@src/lib/icons/18px/file-outline";
import UserOutlineIcon from "@src/lib/icons/18px/user-outline";
import PaginatedTable from "@src/lib/ui/PaginatedTable";
import Table, { FuseSearchConfig } from "@src/lib/ui/Table";
import { Button } from "@src/lib/ui/Button";
import ArrowDownloadIcon from "@src/lib/icons/18px/arrow-download";
import {
  HrisEmployeeFragment,
  useHrisEmployeesLazyQuery,
  useHrisEmployeesQuery,
} from "@src/features/settings/hris/HrisEmployeeListContainer.generated";
import { HrisEmployeesInput } from "@src/types.generated";
import CommaSeparatedLinks from "@src/features/sidebars/ui/CommaSeparatedLinks";
import {
  QueryTabs,
  QueryTabRenderFns,
  QueryTabCopy,
  QueryTabCounts,
} from "@src/lib/ui/QueryTabs";

type Props = {
  rowId: string;
  showHeader?: boolean;
};

const HrisCsvUploadDetailSidebar: React.FC<Props> = ({
  rowId,
  showHeader = true,
}) => {
  const { data } = useHrisCsvUploadSidebarQuery({
    variables: {
      id: rowId,
    },
    skip: !rowId,
  });

  const inputFilters: HrisEmployeesInput = useMemo(() => {
    return {
      filters: {
        hrisCsvUploadId: { value: rowId },
      },
    };
  }, [rowId]);

  // Fetch total count for the tab badge
  const { data: employeesCountData } = useHrisEmployeesQuery({
    variables: {
      input: inputFilters,
      pagination: { limit: 0 }, // Only need the count, not the data
    },
    skip: !rowId,
  });

  const columns = ["name", "hrisId", "status", "location", "role"] as const;
  type Column = (typeof columns)[number];

  const tabCounts: QueryTabCounts<"details" | "employees" | "csvContent"> =
    useMemo(() => {
      return {
        employees: employeesCountData?.HrisEmployees?.totalCount ?? undefined,
      };
    }, [employeesCountData]);

  const upload = data?.HrisCsvUpload;
  if (!upload) return null;

  const handleDownloadCsv = () => {
    if (!upload.csvRawText) return;

    const blob = new Blob([upload.csvRawText], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `hris_upload_${upload.id}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const tabKeys = ["details", "employees", "csvContent"] as const;
  type TabKey = (typeof tabKeys)[number];

  const tabLabels: QueryTabCopy<TabKey> = {
    details: "Details",
    employees: "Modified employees",
    csvContent: "CSV content",
  };

  const tabQueryTabRenderFns: QueryTabRenderFns<TabKey> = {
    details: () => <InfoTable items={detailsItems} />,
    employees: () => (
      <PaginatedTable<
        HrisEmployeeFragment,
        Column,
        typeof useHrisEmployeesQuery,
        typeof useHrisEmployeesLazyQuery,
        Column
      >
        defaultSort={{ column: "name", descending: true }}
        queryHook={useHrisEmployeesQuery}
        lazyQueryHook={useHrisEmployeesLazyQuery}
        getItems={(x) => x?.HrisEmployees?.objects ?? []}
        getTotalItemCount={(x) => x?.HrisEmployees?.totalCount ?? 0}
        itemId={(x) => x.id}
        input={inputFilters}
        sidebarConfig={{
          tableId: "hris-csv-upload-employees",
          sidebarType: DetailSidebarType.HrisEmployee,
          enabled: true,
        }}
        disableRowClickOnColumns={new Set(["location", "role"])}
        allColumns={[...columns]}
        columnSelection={{
          enabled: true,
          alwaysVisibleColumns: ["name"],
          defaultSelectedColumns: [...columns],
        }}
        columnTitles={{
          name: "Name",
          hrisId: "HRIS ID",
          status: "HRIS Status",
          location: "HRIS Location",
          role: "HRIS Role",
        }}
        renderFns={{
          name: (x) => (
            <div className="flex flex-1 items-center gap-2">
              <UserOutlineIcon className="text-gray-7" />
              <Text
                variant="compact-title"
                ellipsis
                ellipsisParentFlexDirection={"row"}
              >
                {`${x.firstName} ${x.lastName}`}
              </Text>
            </div>
          ),
          status: (x) => x.employmentStatus,
          location: (x) => (
            <div className="flex flex-1 items-center gap-1">
              {x.locationMemberships.length > 0 && (
                <CommaSeparatedLinks
                  items={x.locationMemberships.map((memb) => ({
                    id: memb.location.id,
                    name: memb.location.name,
                  }))}
                  sidebarType={DetailSidebarType.HrisLocation}
                  tableId="hris-locations"
                />
              )}
            </div>
          ),
          role: (x) => (
            <CommaSeparatedLinks
              items={x.teamMemberships.map((memb) => ({
                id: memb.team.id,
                name: memb.team.name,
              }))}
              sidebarType={DetailSidebarType.HrisRole}
              tableId="hris-roles"
            />
          ),
          hrisId: (x) => x.remoteHrisId,
        }}
      />
    ),
    csvContent: () => {
      if (!upload.csvRawText) {
        return (
          <div className="p-4">
            <div className="text-gray-7">No CSV content available</div>
          </div>
        );
      }

      // Parse CSV content
      const lines = upload.csvRawText.split("\n").filter((line) => line.trim());
      if (lines.length === 0) {
        return (
          <div className="p-4">
            <div className="text-gray-7">CSV file is empty</div>
          </div>
        );
      }

      const headers = lines[0].split(",").map((h) => h.trim());
      const rows = lines.slice(1).map((line, index) => {
        const values = line.split(",").map((v) => v.trim());
        return {
          ...headers.reduce(
            (obj, header, idx) => {
              obj[header] = values[idx] || "";
              return obj;
            },
            {} as Record<string, string>,
          ),
          __index: String(index),
        };
      });

      type Column = (typeof headers)[number];
      const allColumns = headers as Column[];

      const renderFns: Record<
        Column,
        (row: Record<string, string>) => React.ReactNode
      > = Object.fromEntries(
        headers.map((header) => [
          header,
          (row: Record<string, string>) => row[header] || "",
        ]),
      ) as Record<Column, (row: Record<string, string>) => React.ReactNode>;

      const columnTitles: Record<Column, string> = Object.fromEntries(
        headers.map((header) => [header, header]),
      ) as Record<Column, string>;

      // Create Fuse search config that searches across all column values
      const fuseSearchConfig: FuseSearchConfig<
        Record<string, string> & { __index: string }
      > = {
        threshold: 0.3,
        keys: headers.map((header) => ({
          name: header,
          getFn: (row) => row[header] || "",
        })),
      };

      return (
        <Table<Record<string, string> & { __index: string }, Column>
          title="CSV content"
          allItems={rows}
          allColumns={allColumns}
          itemId={(row) => row.__index ?? JSON.stringify(row)}
          columnTitles={columnTitles}
          renderFns={renderFns}
          showCount={true}
          fuseSearchConfig={fuseSearchConfig}
          columnSelection={{
            enabled: true,
            defaultSelectedColumns: allColumns,
          }}
          sidebarConfig={{
            tableId: "hris-csv-upload-content",
            sidebarType: DetailSidebarType.HrisCsvUpload,
            enabled: false, // Don't enable sidebar navigation, just use for scoped search
          }}
          noDataEmptyState="CSV file is empty"
        />
      );
    },
  };

  const detailsItems: InfoTableItem[] = [
    {
      label: "Sync ID",
      value: upload.id,
    },
    {
      label: "Uploaded by",
      value: upload.uploadedBy ? (
        <SidebarLink
          id={upload.uploadedBy.id}
          sidebarType={DetailSidebarType.User}
          tableId="hris-csv-uploads"
        >
          {upload.uploadedBy.name}
        </SidebarLink>
      ) : (
        "sftp"
      ),
    },
    {
      label: "Location group",
      value: upload.locationGroup ? (
        <Link
          href={{
            pathname: "/locations/[id]",
            query: {
              id: upload.locationGroup.id,
            },
          }}
          className={cn("flex items-center no-underline text-[unset]")}
          target="_blank"
          rel="noopener noreferrer"
        >
          <ClickableLinkText>{upload.locationGroup.name}</ClickableLinkText>
        </Link>
      ) : (
        "--"
      ),
    },
    {
      label: "CSV file name",
      value: upload.csvUrl.split("/").pop(),
    },
    {
      label: "Modified employees",
      value: upload.modifiedEmployeesCount?.toString() ?? "--",
    },
    {
      label: "Applied at",
      value: upload.appliedAt
        ? format(parseISO(upload.appliedAt), "M/d/yyyy h:mm a")
        : "--",
    },
    {
      label: "Data verified at",
      value: upload.dataVerifiedAt
        ? format(parseISO(upload.dataVerifiedAt), "M/d/yyyy h:mm a")
        : "--",
    },
    {
      label: "Columns selected at",
      value: upload.selectedColumnsAt
        ? format(parseISO(upload.selectedColumnsAt), "M/d/yyyy h:mm a")
        : "--",
    },
    {
      label: "Started creating objects at",
      value: upload.startedCreatingObjectsAt
        ? format(parseISO(upload.startedCreatingObjectsAt), "M/d/yyyy h:mm a")
        : "--",
    },
    {
      label: "Finished creating objects at",
      value: upload.finishedCreatingObjectsAt
        ? format(parseISO(upload.finishedCreatingObjectsAt), "M/d/yyyy h:mm a")
        : "--",
    },
    {
      label: "Created at",
      value: upload.createdAt
        ? format(parseISO(upload.createdAt), "M/d/yyyy h:mm a")
        : "--",
    },
    {
      label: "Updated at",
      value: upload.updatedAt
        ? format(parseISO(upload.updatedAt), "M/d/yyyy h:mm a")
        : "--",
    },
  ];

  return (
    <div className="flex h-full flex-col">
      {showHeader && <SidebarHeader />}
      <SidebarTitle
        title={upload.id}
        icon={<FileOutlineIcon className="size-[18px] text-gray-9" />}
        sidebarType={DetailSidebarType.HrisCsvUpload}
      />
      <div className="px-4">
        <div className="flex gap-1">
          <Button
            variant="outline"
            onClick={handleDownloadCsv}
            disabled={!upload.csvRawText}
          >
            <ArrowDownloadIcon className="text-muted-foreground" />
            Download CSV
          </Button>
        </div>
      </div>
      <SidebarContent>
        <div className="flex min-h-0 flex-1 flex-col">
          <QueryTabs<TabKey>
            tabs={[...tabKeys]}
            labels={tabLabels}
            render={tabQueryTabRenderFns}
            counts={tabCounts as QueryTabCounts<TabKey>}
            defaultTab="details"
            queryParamKey="hris-csv-upload-tab"
          />
        </div>
      </SidebarContent>
    </div>
  );
};

gql`
  fragment HrisCsvUploadDetail on HrisCsvUpload {
    id
    createdAt
    updatedAt
    uploadedBy {
      id
      name
    }
    locationGroup {
      id
      name
    }
    selectedColumnsAt
    dataVerifiedAt
    appliedAt
    startedCreatingObjectsAt
    finishedCreatingObjectsAt
    modifiedEmployeesCount
    idField
    firstNameField
    firstNameFallbackField
    lastNameField
    lastNameFallbackField
    nameField
    locationField
    teamField
    phoneField
    phoneFallbackField
    terminationDateField
    startDateField
    csvRawText
    csvUrl
    org {
      id
    }
  }
  query HrisCsvUploadSidebar($id: UUID!) {
    HrisCsvUpload(id: $id) {
      ...HrisCsvUploadDetail
    }
  }
`;

export default HrisCsvUploadDetailSidebar;
